/**
 * Coins — the money-routing hub. Everything about how funds flow:
 *
 *   1. Settlement      — how you get paid (receive natively, or settle every
 *                        payment to one token) + the settlement token/address.
 *   2. Receive chains  — your configured receiving keys, with per-chain
 *                        enable/disable + inline edit (guarded: you can't
 *                        disable the chain your settlement token lives on).
 *   3. Pay-in coins    — optional non-EVM coins customers can pay with
 *                        (Tron/Solana/NEAR/Litecoin/Dash). "Connect keystore"
 *                        re-derives every refund primitive from the account
 *                        seed in-browser; one click enables them all.
 *
 * The seed never leaves the device — only public addresses / xpubs are saved.
 */

import { useCallback, useEffect, useState } from "react";
import {
  ApiError,
  getMyMerchant,
  setAnyCryptoEnabled,
  setChainEnabled,
  setConfidentialMode,
  setDirectFallbackEnabled,
  setSwapRefundAddresses,
  upsertMyMerchant,
  type MerchantProfile,
} from "../lib/api";
import {
  importKeystore,
  deriveAllRefunds,
  type DerivedRefunds,
  type Keystore,
} from "../lib/key-gen";
import { Banner, Btn, CHAINS, Toggle } from "../components/ui";
import { Ic } from "../components/Icons";
import { useNavigate } from "@tanstack/react-router";
import { useT } from "../lib/i18n";

// ── Pay-in coin catalog (non-EVM; need a refund primitive) ──────────────
type PayinDef = {
  key: string;
  /** Display name (a chain/brand — not translated, rendered as-is). */
  title: string;
  coins: string;
  kind: "extern" | "utxo";
  dot: string;
  /** Keystore-derived refund key. Omitted for coins we can't derive from the
   *  seed (e.g. TON contract addresses) — those are custom-address only. */
  derive?: keyof DerivedRefunds;
  placeholder?: string;
};
const PAYIN: PayinDef[] = [
  { key: "TRON", title: "Tron", coins: "USDT", kind: "extern", dot: "#eb0029", derive: "tron", placeholder: "T…" },
  { key: "SOL", title: "Solana", coins: "SOL · USDC · USDT", kind: "extern", dot: "#9945ff", derive: "solana", placeholder: "Solana address…" },
  { key: "NEAR", title: "NEAR", coins: "NEAR", kind: "extern", dot: "#00ec97", derive: "near", placeholder: "name.near or 64-hex" },
  { key: "LTC", title: "Litecoin", coins: "LTC", kind: "extern", dot: "#345d9d", derive: "ltc", placeholder: "ltc1…" },
  { key: "DASH", title: "Dash", coins: "DASH", kind: "extern", dot: "#008ce7", derive: "dash", placeholder: "X…" },
  { key: "BCH", title: "Bitcoin Cash", coins: "BCH", kind: "extern", dot: "#0ac18e", derive: "bch", placeholder: "bitcoincash:q…" },
  { key: "ZEC", title: "Zcash (transparent)", coins: "ZEC", kind: "extern", dot: "#f4b728", derive: "zec", placeholder: "t1…" },
  // TON: contract-address chain — can't derive from the seed without the TON
  // SDK, so it's custom-address only (paste your Tonkeeper address).
  { key: "TON", title: "Toncoin", coins: "TON", kind: "extern", dot: "#0098ea", placeholder: "EQ… / UQ…" },
];

const trunc = (s: string): string =>
  s.length > 16 ? `${s.slice(0, 8)}…${s.slice(-6)}` : s;

const chainName = (id: string): string => CHAINS.find((c) => c.id === id)?.name ?? id;
const chainLabel = (id: string): string =>
  ({ ZEC: "Zcash", BTC: "Bitcoin", BASE: "USDC on Base", ARB: "USDC on Arbitrum", OP: "USDC on Optimism", POLY: "USDC on Polygon", ETH: "USDC on Ethereum", LTC: "Litecoin", DASH: "Dash" } as Record<string, string>)[id] ?? id;
const chainAbbr = (id: string): string =>
  ({ BASE: "B", ARB: "A", POLY: "P", ETH: "E", OP: "O", BTC: "₿", ZEC: "Z", LTC: "L", DASH: "D" } as Record<string, string>)[id] ?? "?";
const chainColor = (id: string): string =>
  ({ ZEC: "#08a072", BTC: "#f7931a", BASE: "#0052ff", ARB: "#28a0f0", OP: "#ff0420", POLY: "#8247e5", ETH: "#627eea", LTC: "#345d9d", DASH: "#008ce7" } as Record<string, string>)[id] ?? "#7b8794";

export function Coins() {
  const t = useT();
  const navigate = useNavigate();
  const [merchant, setMerchant] = useState<MerchantProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  // Settlement section state.
  const [oneToken, setOneToken] = useState(false);
  const [directFallback, setDirectFallback] = useState(false);
  const [fbOpen, setFbOpen] = useState(false);
  const [confMode, setConfMode] = useState<"off" | "preferred" | "only">("off");
  const [confOpen, setConfOpen] = useState(false);
  const [slipOpen, setSlipOpen] = useState(false);
  const [chainId, setChainId] = useState<string>(CHAINS[0]!.id);
  const [settleSymbol, setSettleSymbol] = useState<"USDC" | "USDT">("USDC");
  const [address, setAddress] = useState("");
  const [slippage, setSlippage] = useState(100);
  const [settleBusy, setSettleBusy] = useState(false);
  const [showInterstitial, setShowInterstitial] = useState(false);

  // Receive-chain inline edit.
  const [editChain, setEditChain] = useState<string | null>(null);
  const [editVal, setEditVal] = useState("");

  // Keystore-derived primitives (in-session; never persisted).
  const [derived, setDerived] = useState<DerivedRefunds | null>(null);
  const [ksOpen, setKsOpen] = useState(false);
  const [ksFile, setKsFile] = useState<Keystore | null>(null);
  const [ksFileName, setKsFileName] = useState("");
  const [ksPass, setKsPass] = useState("");
  const [ksBusy, setKsBusy] = useState(false);
  const [ksErr, setKsErr] = useState<string | null>(null);
  const [derivedMismatch, setDerivedMismatch] = useState<string | null>(null);

  // Pay-in custom address editor.
  const [customKey, setCustomKey] = useState<string | null>(null);
  const [customVal, setCustomVal] = useState("");

  const load = useCallback((m: MerchantProfile) => {
    setMerchant(m);
    setOneToken(m.any_crypto_enabled === true);
    setDirectFallback(m.direct_fallback_enabled === true);
    setConfMode(m.confidential_mode ?? "off");
    setChainId(m.default_settlement?.chain ?? CHAINS[0]!.id);
    setSettleSymbol(
      (m.default_settlement?.asset ?? "").toUpperCase().includes("USDT") ? "USDT" : "USDC",
    );
    setAddress(m.default_settlement?.address ?? "");
    setSlippage(m.slippage_bps ?? 100);
  }, []);

  const refresh = useCallback(async () => {
    try {
      load(await getMyMerchant());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [load]);

  useEffect(() => { void refresh(); }, [refresh]);

  if (!merchant) {
    return (
      <div className="scroll">
        <div className="pad" style={{ color: "var(--ink-3)" }}>{t("coins.loading")}</div>
      </div>
    );
  }

  const refunds = merchant.swap_refund_addresses ?? {};
  const settlementReady = /^0x[0-9a-fA-F]{40}$/.test(address.trim());
  const settleChain = (merchant.default_settlement?.chain ?? "").toUpperCase();

  // ── generic action runner ──────────────────────────────────────────
  const run = async (key: string, fn: () => Promise<unknown>) => {
    setError(null);
    setBusyKey(key);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  };

  // ── settlement ──────────────────────────────────────────────────────
  const setMode = async (next: boolean) => {
    setOneToken(next);
    setError(null);
    try {
      await setAnyCryptoEnabled(next);
      setSavedAt(Date.now());
    } catch (e) {
      setOneToken(!next);
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const setFallback = async (next: boolean) => {
    setDirectFallback(next);
    setError(null);
    try {
      await setDirectFallbackEnabled(next);
      setSavedAt(Date.now());
    } catch (e) {
      setDirectFallback(!next);
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const saveConfMode = async (next: "off" | "preferred" | "only") => {
    const prev = confMode;
    setConfMode(next);
    setError(null);
    try {
      await setConfidentialMode(next);
      setSavedAt(Date.now());
    } catch (e) {
      setConfMode(prev);
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const saveSettlement = async () => {
    if (!settlementReady) {
      setError(t("coins.err_settlement_address_0x"));
      return;
    }
    if (merchant.default_settlement?.address !== address.trim() && !showInterstitial) {
      setShowInterstitial(true);
      return;
    }
    setError(null);
    setSettleBusy(true);
    try {
      const chain = CHAINS.find((c) => c.id === chainId)!;
      await upsertMyMerchant({
        display_name: merchant.display_name ?? "Merchant",
        settlement: { chain: chain.id, asset: `${chain.id}.${settleSymbol}`, address: address.trim() },
        slippage_bps: slippage,
      });
      setSavedAt(Date.now());
      setShowInterstitial(false);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSettleBusy(false);
    }
  };

  // ── receive chains ──────────────────────────────────────────────────
  const toggleReceive = (chain: string, next: boolean) =>
    run(`rc:${chain}`, () => setChainEnabled(chain, next));

  const saveEdit = (chain: string, kind: "ufvk" | "xpub" | "address") => {
    const v = editVal.trim();
    if (!v) return;
    return run(`rc:${chain}`, async () => {
      await upsertMyMerchant({
        display_name: merchant.display_name ?? "Merchant",
        chains: [{ chain_id: chain, primitive_kind: kind, primitive_value: v }],
      });
      setEditChain(null);
      setEditVal("");
    });
  };

  // ── pay-in coins ────────────────────────────────────────────────────
  const isPayinOn = (d: PayinDef): boolean =>
    d.kind === "extern"
      ? !!refunds[d.key]?.trim()
      : !!merchant.chains?.some((c) => c.chain_id === d.key && c.enabled);
  const payinValue = (d: PayinDef): string | null =>
    d.kind === "extern"
      ? (refunds[d.key]?.trim() || null)
      : (merchant.chains?.find((c) => c.chain_id === d.key && c.enabled)?.primitive_fingerprint ?? null);
  const isPayinDerived = (d: PayinDef): boolean =>
    !!derived && !!d.derive && refunds[d.key]?.trim() === derived[d.derive];

  // All non-EVM coins are extern: a single refund address in
  // swap_refund_addresses (keystore-derived, or custom). Coins without a
  // `derive` (TON) are custom-address only.
  const enablePayin = (d: PayinDef) => {
    if (!derived || !d.derive) return;
    return run(d.key, () => setSwapRefundAddresses({ [d.key]: derived[d.derive!] }));
  };
  const disablePayin = (d: PayinDef) =>
    run(d.key, () => setSwapRefundAddresses({ [d.key]: "" }));
  const togglePayin = (d: PayinDef, next: boolean) => {
    if (!next) return void disablePayin(d);
    if (derived && d.derive) return void enablePayin(d);
    // No keystore derive (TON, or keystore not connected) → custom address.
    setCustomKey(d.key);
    setCustomVal("");
  };
  const saveCustom = (d: PayinDef) => {
    const v = customVal.trim();
    if (!v) return;
    return run(d.key, async () => {
      await setSwapRefundAddresses({ [d.key]: v });
      setCustomKey(null); setCustomVal("");
    });
  };

  // ── keystore connect ────────────────────────────────────────────────
  const onKsFile = async (file: File | null) => {
    setKsErr(null);
    if (!file) { setKsFile(null); setKsFileName(""); return; }
    try {
      const blob = JSON.parse(await file.text()) as Keystore;
      if (typeof blob?.ct_b64 !== "string" || typeof blob?.version !== "number") throw new Error("bad");
      setKsFile(blob); setKsFileName(file.name);
    } catch {
      setKsFile(null); setKsFileName("");
      setKsErr(t("coins.err_not_keystore_json"));
    }
  };
  const connectKeystore = async () => {
    if (!ksFile) return;
    setKsErr(null); setKsBusy(true);
    let mnemonic = "";
    try {
      mnemonic = await importKeystore(ksFile, ksPass);
    } catch {
      setKsBusy(false);
      setKsErr(t("coins.err_wrong_passphrase"));
      return;
    }
    try {
      const all = deriveAllRefunds(mnemonic);
      // Non-blocking sanity note: if the keystore's wallet isn't this account's
      // settlement address, it's usually fine (you may settle to an exchange /
      // cold wallet) — the refund addresses just need to be wallets you control.
      // We derive regardless and only warn.
      const settle = merchant.default_settlement?.address ?? "";
      setDerivedMismatch(
        settle && all.evm.toLowerCase() !== settle.toLowerCase()
          ? `${t("coins.mismatch_prefix")} (${all.evm.slice(0, 6)}…${all.evm.slice(-4)}) ${t("coins.mismatch_middle")} (${settle.slice(0, 6)}…${settle.slice(-4)}). ${t("coins.mismatch_suffix")}`
          : null,
      );
      setDerived(all);
      setKsPass(""); setKsFile(null); setKsFileName(""); setKsOpen(false);
    } finally {
      mnemonic = "";
      setKsBusy(false);
    }
  };
  const enableAllDerived = () => {
    if (!derived) return;
    // Build from the catalog so every derivable coin is included (and new ones
    // can't be forgotten). Custom-only coins (no `derive`, e.g. TON) are
    // excluded — they're enabled manually.
    const addrs = Object.fromEntries(
      PAYIN.filter((d) => d.derive).map((d) => [d.key, derived[d.derive!]]),
    );
    return run("__all__", () => setSwapRefundAddresses(addrs));
  };

  // Only derivable coins count toward "Enable all N" — custom-only coins
  // (TON) can't be auto-enabled, so they'd otherwise wedge the count > 0.
  const payinOffCount = PAYIN.filter((d) => d.derive && !isPayinOn(d)).length;

  // Unified "receive chains" list = configured native chains (EVM/BTC/ZEC,
  // rendered from merchant.chains) + the optional non-EVM coins below, all
  // extern (single refund address each).
  const externDefs = PAYIN;

  // One row for an optional non-EVM coin (Tron/Solana/NEAR/Litecoin/Dash),
  // same look as a configured chain row.
  const renderPayinRow = (d: PayinDef) => {
    const on = isPayinOn(d);
    const val = payinValue(d);
    const editing = customKey === d.key;
    return (
      <div key={`payin:${d.key}`} className="li" style={{ flexWrap: "wrap" }}>
        <div className="li-ic" style={{ background: d.dot, color: "#fff", fontWeight: 700, fontSize: 11 }}>
          {d.title[0]}
        </div>
        <div className="li-main">
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {d.title}
            <span style={{ color: "var(--ink-3)", fontWeight: 400, fontSize: 13 }}> · {d.coins}</span>
          </div>
          <div className="li-meta">
            {on && val ? (
              <>
                {t("coins.refund_lower")}{" "}
                <span className="mono">{trunc(val)}</span>
                {" · "}
                {isPayinDerived(d) ? t("coins.from_keystore") : t("coins.custom_lower")}
              </>
            ) : on ? t("coins.enabled")
              : !d.derive ? t("coins.off_set_custom")
              : derived ? t("coins.off_derived_ready")
              : t("coins.off_connect_keystore")}
          </div>
        </div>
        <div className="li-right" style={{ gap: 10 }}>
          {d.kind === "extern" && (
            <button type="button"
              onClick={() => { setCustomKey(editing ? null : d.key); setCustomVal(on && !isPayinDerived(d) ? (val ?? "") : ""); }}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink-3)", fontSize: 12.5 }}>
              {editing ? t("coins.close") : t("coins.custom")}
            </button>
          )}
          {busyKey === d.key ? <span className="li-meta">…</span> : <Toggle on={on} onChange={(next) => togglePayin(d, next)} ariaLabel={`${t("coins.toggle")} ${d.title}`} />}
        </div>
        {editing && d.kind === "extern" && (
          <div style={{ flexBasis: "100%", marginTop: 10 }}>
            <div className="field">
              <input className="input" type="text" spellCheck={false} autoCapitalize="off" autoCorrect="off" placeholder={d.placeholder} value={customVal} onChange={(e) => setCustomVal(e.target.value)} />
              <div className="help">{t("coins.custom_addr_help_1")} {d.title} {t("coins.custom_addr_help_2")}</div>
            </div>
            <div className="row" style={{ marginTop: 8 }}>
              <Btn onClick={() => void saveCustom(d)} disabled={busyKey === d.key || !customVal.trim()}>{busyKey === d.key ? t("coins.saving") : t("coins.save_address")}</Btn>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="scroll">
      <div className="pad fade-in">
        {error && <div style={{ marginBottom: 12 }}><Banner tone="fail">{error}</Banner></div>}
        {savedAt && (
          <div style={{ marginBottom: 12 }}>
            <Banner tone="ok">{t("coins.saved_at")} {new Date(savedAt).toLocaleTimeString()}</Banner>
          </div>
        )}

        {/* ── 1. Settlement ───────────────────────────────────────── */}
        <div className="s-label">{t("coins.how_you_get_paid")}</div>
        <div className="card" style={{ marginBottom: 14 }}>
          <button type="button" className={`mode-opt${!oneToken ? " on" : ""}`} onClick={() => void setMode(false)}>
            <span className="mode-radio" />
            <span className="mode-main">
              <span className="mode-title">{t("coins.mode_native_title")}</span>
              <span className="mode-tag">{t("coins.mode_native_desc")}</span>
            </span>
          </button>
          <button type="button" className={`mode-opt${oneToken ? " on" : ""}`} onClick={() => void setMode(true)}>
            <span className="mode-radio" />
            <span className="mode-main">
              <span className="mode-title">{t("coins.mode_one_token_title")}</span>
              <span className="mode-tag">{t("coins.mode_one_token_desc")}</span>
            </span>
          </button>
        </div>

        {/* Direct-settlement fallback — always visible (applies to swap-in
            payments under one-token mode). */}
        <div className="s-label">{t("coins.small_payment_fallback")}</div>
        <div className="fb-card">
          <div className="fb-head">
            <div className="fb-ic">
              <Ic name="zap" size={16} className="ic" />
            </div>
            <div className="fb-main">
              <div className="fb-title">{t("coins.fb_title")}</div>
              <div className="fb-sub">
                {t("coins.fb_sub")}
              </div>
            </div>
            <Toggle
              on={directFallback}
              onChange={(next) => void setFallback(next)}
              ariaLabel={t("coins.fb_toggle_aria")}
            />
          </div>

          <button
            type="button"
            className={`fb-toggle${fbOpen ? " open" : ""}`}
            onClick={() => setFbOpen((v) => !v)}
            aria-expanded={fbOpen}
          >
            <Ic name="chevron" size={15} className="ic" />
            {fbOpen ? t("coins.hide_details") : t("coins.how_it_works")}
          </button>

          {fbOpen && (
            <div className="fb-panel">
              <p className="fb-p">
                {t("coins.fb_p_1")} <b>{t("coins.fb_p_bold_1")}</b> {t("coins.fb_p_2")} <b>{t("coins.fb_p_bold_2")}</b>.
              </p>

              <div className="fb-flow">
                <div className="fb-flow-row">
                  <span className="fb-chip off">{t("coins.fb_chip_off")}</span>
                  <span className="fb-arrow">→</span>
                  <span className="fb-flow-txt">{t("coins.fb_flow_off")} <b className="fb-fail">{t("coins.fb_flow_off_bold")}</b></span>
                </div>
                <div className="fb-flow-row">
                  <span className="fb-chip on">{t("coins.fb_chip_on")}</span>
                  <span className="fb-arrow">→</span>
                  <span className="fb-flow-txt">{t("coins.fb_flow_on")} <b className="fb-ok">{t("coins.fb_flow_on_bold")}</b></span>
                </div>
              </div>

              <div className="fb-grid">
                <div className="fb-item">
                  <div className="fb-item-h"><Ic name="check" size={13} className="ic" /> {t("coins.fb_item_watchable_h")}</div>
                  <div className="fb-item-b">{t("coins.fb_item_watchable_b_1")} <b>BTC</b> {t("coins.fb_item_watchable_b_2")} <b>DOGE</b> {t("coins.fb_item_watchable_b_3")}</div>
                </div>
                <div className="fb-item">
                  <div className="fb-item-h"><Ic name="lock" size={13} className="ic" /> {t("coins.fb_item_noncustodial_h")}</div>
                  <div className="fb-item-b">{t("coins.fb_item_noncustodial_b")}</div>
                </div>
                <div className="fb-item">
                  <div className="fb-item-h"><Ic name="info" size={13} className="ic" /> {t("coins.fb_item_tradeoff_h")}</div>
                  <div className="fb-item-b">{t("coins.fb_item_tradeoff_b_1")} <b>{t("coins.fb_item_tradeoff_b_bold")}</b> {t("coins.fb_item_tradeoff_b_2")}</div>
                </div>
                <div className="fb-item">
                  <div className="fb-item-h"><Ic name="zap" size={13} className="ic" /> {t("coins.fb_item_triggers_h")}</div>
                  <div className="fb-item-b">{t("coins.fb_item_triggers_b")}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Confidential routing — shield swap-in payments over NEAR's private
            intents. Applies to swap-in ("pay with any crypto") payments. */}
        <div className="s-label">{t("coins.confidential_payments")}</div>
        <div className="fb-card">
          <div className="fb-head">
            <div className="fb-ic">
              <Ic name="lock" size={16} className="ic" />
            </div>
            <div className="fb-main">
              <div className="fb-title">{t("coins.conf_title")}</div>
              <div className="fb-sub">
                {t("coins.conf_sub")}
              </div>
            </div>
          </div>

          <div
            role="radiogroup"
            aria-label={t("coins.conf_radiogroup_aria")}
            style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}
          >
            {([
              { key: "off", title: t("coins.conf_off_title"), tag: t("coins.conf_off_tag") },
              { key: "preferred", title: t("coins.conf_preferred_title"), tag: t("coins.conf_preferred_tag") },
              { key: "only", title: t("coins.conf_only_title"), tag: t("coins.conf_only_tag") },
            ] as const).map((o) => {
              const active = confMode === o.key;
              return (
                <button
                  key={o.key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => void saveConfMode(o.key)}
                  style={{
                    flex: "1 1 150px",
                    textAlign: "left",
                    padding: "11px 13px",
                    borderRadius: 11,
                    cursor: "pointer",
                    border: active
                      ? "1.5px solid var(--brand-500)"
                      : "1.5px solid var(--line, #e6e8eb)",
                    background: active ? "var(--brand-50, #ecfdf5)" : "var(--surface, #fff)",
                    transition: "border-color .12s, background .12s",
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 13.5, color: "var(--ink, #111827)" }}>
                    {o.title}
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 2, lineHeight: 1.35 }}>
                    {o.tag}
                  </div>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className={`fb-toggle${confOpen ? " open" : ""}`}
            onClick={() => setConfOpen((v) => !v)}
            aria-expanded={confOpen}
          >
            <Ic name="chevron" size={15} className="ic" />
            {confOpen ? t("coins.hide_details") : t("coins.how_it_works")}
          </button>

          {confOpen && (
            <div className="fb-panel">
              <p className="fb-p">
                {t("coins.conf_p_1")} <b>{t("coins.conf_p_bold_1")}</b> {t("coins.conf_p_2")} <b>{t("coins.conf_p_bold_2")}</b>{t("coins.conf_p_3")}
              </p>
              <div className="fb-grid">
                <div className="fb-item">
                  <div className="fb-item-h"><Ic name="check" size={13} className="ic" /> {t("coins.conf_item_available_h")}</div>
                  <div className="fb-item-b">{t("coins.conf_item_available_b")}</div>
                </div>
                <div className="fb-item">
                  <div className="fb-item-h"><Ic name="lock" size={13} className="ic" /> {t("coins.conf_item_only_h")}</div>
                  <div className="fb-item-b">{t("coins.conf_item_only_b")}</div>
                </div>
                <div className="fb-item">
                  <div className="fb-item-h"><Ic name="info" size={13} className="ic" /> {t("coins.conf_item_coverage_h")}</div>
                  <div className="fb-item-b">{t("coins.conf_item_coverage_b")}</div>
                </div>
                <div className="fb-item">
                  <div className="fb-item-h"><Ic name="zap" size={13} className="ic" /> {t("coins.conf_item_noncustodial_h")}</div>
                  <div className="fb-item-b">{t("coins.conf_item_noncustodial_b")}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {oneToken && (
          <>
            <div className="s-label">{t("coins.settlement_token")}</div>
            <div className="card" style={{ marginBottom: 14 }}>
              <div className="ac-body">
                <div className="ac-assure">
                  <span className="pill-trust"><Ic name="lock" size={13} className="ic" /> {t("coins.pill_noncustodial")}</span>
                  <span className="pill-trust"><Ic name="zap" size={13} className="ic" /> {t("coins.pill_feeless")}</span>
                </div>
                <div className="set-label">{t("coins.settlement_chain")}</div>
                <div className="set-chains">
                  {CHAINS.map((c) => (
                    <button key={c.id} type="button" className={`set-chain${chainId === c.id ? " on" : ""}`} onClick={() => setChainId(c.id)}>
                      <span className="set-dot" style={{ background: c.color }} />
                      {c.name}
                    </button>
                  ))}
                </div>
                <div className="set-label" style={{ marginTop: 16 }}>{t("coins.token")}</div>
                <div className="set-seg">
                  {(["USDC", "USDT"] as const).map((s) => (
                    <button key={s} type="button" className={`set-seg-opt${settleSymbol === s ? " on" : ""}`} onClick={() => setSettleSymbol(s)}>{s}</button>
                  ))}
                </div>
                <div className="field" style={{ marginTop: 16 }}>
                  <label htmlFor="settle-addr">{settleSymbol} {t("coins.address_on")} {chainName(chainId)}</label>
                  <input
                    id="settle-addr"
                    className={`input${address && !settlementReady ? " err" : ""}`}
                    type="text" spellCheck={false} autoCapitalize="off" autoCorrect="off"
                    placeholder="0x…" value={address} onChange={(e) => setAddress(e.target.value)}
                  />
                  <div className="help">{t("coins.settle_addr_help")}</div>
                </div>
                <div className="row" style={{ marginTop: 12 }}>
                  <Btn onClick={() => void saveSettlement()} disabled={settleBusy || !settlementReady}>
                    {settleBusy ? t("coins.saving") : t("coins.save_settlement")}
                  </Btn>
                </div>
                <div className="ac-note">
                  <Ic name="shield" size={15} className="ic" />
                  <div>
                    {settlementReady ? (
                      <>{t("coins.settle_note_all_1")} <b style={{ color: "var(--ink)" }}>{settleSymbol} {t("coins.address_on")} {chainName(chainId)}</b> {t("coins.settle_note_all_2")} <span className="mono">{trunc(address)}</span>.</>
                    ) : (<>{t("coins.settle_note_invalid_1")} <b>{settleSymbol}</b> {t("coins.settle_note_invalid_2")}</>)}
                  </div>
                </div>
              </div>
            </div>

            <div className="s-label">{t("coins.slippage_tolerance")}</div>
            <div className="fb-card" style={{ marginBottom: 18 }}>
              <div className="slip-body">
                <div className="slip-top">
                  <div>
                    <div className="slip-label">{t("coins.max_slippage")}</div>
                    <div className="slip-desc">{t("coins.slip_desc")}</div>
                  </div>
                  <div className="slip-val">
                    <span className="slip-pct tnum">{(slippage / 100).toFixed(2)}%</span>
                    <span className="slip-bps tnum">{slippage} bps</span>
                  </div>
                </div>

                <div className="slip-presets">
                  {([[50, "0.5%"], [100, "1%"], [200, "2%"], [300, "3%"]] as [number, string][]).map(([v, l]) => (
                    <button
                      key={v}
                      type="button"
                      className={`slip-chip${slippage === v ? " active" : ""}`}
                      onClick={() => setSlippage(v)}
                    >
                      {l}
                      {v === 100 ? <span className="slip-def">{t("coins.slip_default")}</span> : null}
                    </button>
                  ))}
                </div>

                <input
                  className="slider slip-slider"
                  type="range"
                  min={1}
                  max={500}
                  value={slippage}
                  onChange={(e) => setSlippage(Number(e.target.value))}
                  aria-label={t("coins.slip_slider_aria")}
                />
                <div className="slip-scale"><span>0.01%</span><span>{t("coins.slip_scale_mid")}</span><span>5%</span></div>

                <div className="slip-worst">
                  <Ic name="info" size={13} className="ic" />
                  <span>{t("coins.slip_worst_1")} <b>$100</b> {t("coins.slip_worst_2")} <b className="tnum">${(100 * (1 - slippage / 10000)).toFixed(2)}</b> {t("coins.slip_worst_3")}</span>
                </div>

                <div className="slip-foot">
                  <Btn onClick={() => void saveSettlement()} loading={settleBusy}>{t("coins.save_slippage")}</Btn>
                  {savedAt ? <span className="slip-saved"><Ic name="check" size={13} className="ic" /> {t("coins.saved")}</span> : null}
                </div>
              </div>

              <button
                type="button"
                className={`fb-toggle${slipOpen ? " open" : ""}`}
                onClick={() => setSlipOpen((v) => !v)}
                aria-expanded={slipOpen}
              >
                <Ic name="chevron" size={15} className="ic" />
                {slipOpen ? t("coins.hide_details") : t("coins.what_is_slippage")}
              </button>

              {slipOpen && (
                <div className="fb-panel">
                  <p className="fb-p">
                    {t("coins.slip_p_1")} <b>{t("coins.slip_p_bold")}</b> {t("coins.slip_p_2")}
                  </p>
                  <div className="fb-grid">
                    <div className="fb-item">
                      <div className="fb-item-h"><Ic name="shield" size={13} className="ic" /> {t("coins.slip_item_lower_h")}</div>
                      <div className="fb-item-b">{t("coins.slip_item_lower_b_1")} <b>{t("coins.slip_item_lower_b_bold")}</b> {t("coins.slip_item_lower_b_2")}</div>
                    </div>
                    <div className="fb-item">
                      <div className="fb-item-h"><Ic name="check" size={13} className="ic" /> {t("coins.slip_item_higher_h")}</div>
                      <div className="fb-item-b"><b>{t("coins.slip_item_higher_b_bold")}</b> {t("coins.slip_item_higher_b")}</div>
                    </div>
                    <div className="fb-item">
                      <div className="fb-item-h"><Ic name="zap" size={13} className="ic" /> {t("coins.slip_item_default_h")}</div>
                      <div className="fb-item-b"><b>1%</b> {t("coins.slip_item_default_b")}</div>
                    </div>
                    <div className="fb-item">
                      <div className="fb-item-h"><Ic name="info" size={13} className="ic" /> {t("coins.slip_item_stablecoins_h")}</div>
                      <div className="fb-item-b">{t("coins.slip_item_stablecoins_b")}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </>
        )}

        {/* ── 2. Receive chains ───────────────────────────────────── */}
        <div className="s-label">{t("coins.receive_chains")}</div>
        <div className="card" style={{ marginBottom: 18 }}>
          {(merchant.chains ?? []).map((c) => {
              const locked = c.chain_id === settleChain; // can't disable settlement chain
              const editing = editChain === c.chain_id;
              return (
                <div key={c.chain_id} className="li" style={{ flexWrap: "wrap" }}>
                  <div className="li-ic" style={{ background: chainColor(c.chain_id), color: "#fff", fontWeight: 700, fontSize: 11 }}>
                    {chainAbbr(c.chain_id)}
                  </div>
                  <div className="li-main">
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{chainLabel(c.chain_id)}</div>
                    <div className="li-meta">
                      <span className="mono">{c.primitive_fingerprint}</span>
                      <span style={{ marginLeft: 8, color: "var(--ink-3)" }}>
                        {c.primitive_kind === "ufvk" ? "UFVK" : c.primitive_kind === "xpub" ? "xpub" : t("coins.address")}
                      </span>
                    </div>
                  </div>
                  <div className="li-right" style={{ gap: 10 }}>
                    <button
                      type="button"
                      onClick={() => { setEditChain(editing ? null : c.chain_id); setEditVal(""); }}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink-3)", fontSize: 12.5 }}
                    >
                      {editing ? t("coins.close") : t("coins.edit")}
                    </button>
                    {busyKey === `rc:${c.chain_id}` ? (
                      <span className="li-meta">…</span>
                    ) : locked ? (
                      <span className="pill ok" title={t("coins.settlement_chain_locked")}><span className="pd" />{t("coins.on")}</span>
                    ) : (
                      <Toggle on={c.enabled} onChange={(next) => void toggleReceive(c.chain_id, next)} ariaLabel={`${t("coins.toggle")} ${chainLabel(c.chain_id)}`} />
                    )}
                  </div>
                  {editing && (
                    <div style={{ flexBasis: "100%", marginTop: 10 }}>
                      <div className="field">
                        <input
                          className="input" type="text" spellCheck={false} autoCapitalize="off" autoCorrect="off"
                          placeholder={c.primitive_kind === "ufvk" ? "uview1…" : c.primitive_kind === "xpub" ? "xpub / zpub…" : "0x…"}
                          value={editVal} onChange={(e) => setEditVal(e.target.value)}
                        />
                        <div className="help" style={{ color: "var(--danger)" }}>
                          {t("coins.edit_help_1")} {chainLabel(c.chain_id)} {t("coins.edit_help_2")} {c.primitive_kind === "address" ? t("coins.address") : t("coins.key")}. {t("coins.edit_help_3")}
                        </div>
                      </div>
                      <div className="row" style={{ marginTop: 8 }}>
                        <Btn onClick={() => void saveEdit(c.chain_id, c.primitive_kind)} disabled={busyKey === `rc:${c.chain_id}` || !editVal.trim()}>
                          {busyKey === `rc:${c.chain_id}` ? t("coins.saving") : t("coins.save_verify")}
                        </Btn>
                      </div>
                    </div>
                  )}
                </div>
              );
          })}
          {/* Swap-in coins: these are refund addresses, not receiving ones. */}
          <div className="card-pad help" style={{ borderTop: "1px solid var(--line)", margin: 0 }}>
            {oneToken ? t("coins.payin_note_one_token") : t("coins.payin_note_native")}
          </div>
          {externDefs.map(renderPayinRow)}
          <div className="card-pad" style={{ borderTop: "1px solid var(--line)" }}>
            <Btn variant="secondary" onClick={() => void navigate({ to: "/setup" })}>
              {t("coins.add_or_change_chains")}
            </Btn>
            <div className="help" style={{ marginTop: 8 }}>
              {t("coins.add_chains_help")}
            </div>
          </div>
        </div>

        {/* ── 3. Pay-in coins ─────────────────────────────────────── */}
        {derived ? (
          <div style={{ marginBottom: 14 }}>
            <Banner tone="ok">
              {t("coins.keystore_connected")}{" "}
              {payinOffCount > 0 && (
                <button type="button" onClick={() => void enableAllDerived()} disabled={busyKey === "__all__"}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--brand-700)", fontWeight: 600, textDecoration: "underline" }}>
                  {busyKey === "__all__" ? t("coins.enabling") : `${t("coins.enable_all_1")} ${payinOffCount} ${t("coins.enable_all_2")}`}
                </button>
              )}
            </Banner>
            {derivedMismatch && (
              <div style={{ marginTop: 8 }}><Banner tone="warn">{derivedMismatch}</Banner></div>
            )}
          </div>
        ) : (
          <div className="card card-pad" style={{ marginBottom: 14 }}>
            {!ksOpen ? (
              <div className="set-coin-head" style={{ alignItems: "center" }}>
                <span className="set-dot" style={{ background: "var(--brand-500)" }} />
                <div style={{ flex: 1 }}>
                  <div className="set-coin-title">{t("coins.connect_keystore_title")}</div>
                  <div className="set-coin-sub">{t("coins.connect_keystore_sub")}</div>
                </div>
                <Btn variant="secondary" onClick={() => setKsOpen(true)}>{t("coins.connect")}</Btn>
              </div>
            ) : (
              <>
                <div className="set-coin-title" style={{ marginBottom: 12 }}>{t("coins.connect_keystore_title")}</div>
                <div className="field">
                  <label htmlFor="ks-file">{t("coins.keystore_file")}</label>
                  <label htmlFor="ks-file" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 14px", border: "1px solid var(--line-2)", borderRadius: 13, background: "var(--surface)", cursor: "pointer", fontSize: 13.5, fontWeight: 500, color: "var(--ink-2)", width: "fit-content" }}>
                    <Ic name="key" size={15} />
                    {ksFileName || t("coins.choose_keystore_json")}
                    <input id="ks-file" type="file" accept="application/json,.json" style={{ display: "none" }} onChange={(e) => void onKsFile(e.target.files?.[0] ?? null)} />
                  </label>
                </div>
                <div className="field" style={{ marginTop: 12 }}>
                  <label htmlFor="ks-pass">{t("coins.keystore_passphrase")}</label>
                  <input id="ks-pass" className="input" type="password" autoComplete="off" spellCheck={false} placeholder="••••••••" value={ksPass}
                    onChange={(e) => setKsPass(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && ksFile && ksPass.length >= 8) void connectKeystore(); }} />
                  {!ksFile && <div className="help">{t("coins.choose_keystore_above")}</div>}
                  {ksFile && ksPass.length < 8 && <div className="help">{t("coins.enter_passphrase_hint")}</div>}
                </div>
                {ksErr && <div style={{ marginTop: 12 }}><Banner tone="fail">{ksErr}</Banner></div>}
                <div className="row" style={{ marginTop: 12, gap: 8 }}>
                  <Btn onClick={() => void connectKeystore()} disabled={ksBusy || !ksFile || ksPass.length < 8}>{ksBusy ? t("coins.checking") : t("coins.connect_derive")}</Btn>
                  <Btn variant="ghost" onClick={() => { setKsOpen(false); setKsErr(null); }}>{t("coins.cancel")}</Btn>
                </div>
                <div className="ac-note"><Ic name="lock" size={15} className="ic" /><div>{t("coins.derivation_note")}</div></div>
              </>
            )}
          </div>
        )}

        <div className="help" style={{ marginTop: 12 }}>
          {t("coins.bottom_help")}
        </div>
      </div>

      {showInterstitial && (
        <div className="overlay center show">
          <div className="modal">
            <div style={{ marginBottom: 12 }}>
              <Banner tone="warn">{t("coins.interstitial_warn")}</Banner>
            </div>
            <p style={{ color: "var(--ink-2)", fontSize: 13.5, margin: "0 0 18px", lineHeight: 1.55 }}>
              {t("coins.new_address")} <code className="mono" style={{ fontSize: 12 }}>{address}</code>
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn variant="secondary" block onClick={() => setShowInterstitial(false)}>{t("coins.cancel")}</Btn>
              <Btn block onClick={() => void saveSettlement()} loading={settleBusy}>{t("coins.confirm_change")}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
